import { useHoursData, useTimeSpentDatas } from '@gozoki/api'
import type { DayOfWeek } from '@gozoki/api-types'
import { FormControl, IconButton, MenuItem, Select, Stack, Typography } from '@mui/material'
import { createFileRoute } from '@tanstack/react-router'
import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js'
import { ArrowLeft } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Line } from 'react-chartjs-2'
import { withRoutePermissions } from '~/utils/rbac'
import styles from '../../../assets/styles/DashboardPage.module.css'
import Page from '../../../components/Page'
import { DayPicker } from '../../../utils/dashboard/dayPicker'
import { type DateStateProps, FourDatePicker } from '../../../utils/dashboard/fourDatePicker'
import { StoresPicker } from '../../../utils/dashboard/storesPicker'
import { timeRanges } from '../../../utils/dashboard/timeRanges'
import { fonts } from '../../../utils/theme/fonts.theme'

export const Route = createFileRoute('/_authenticated/dashboard/hours')({
  staticData: {
    permission: ['read', 'dashboard.dashboard.global-indicators'],
  },
  component: DashboardHoursPage,
})
withRoutePermissions(Route)

// Enregistrement des composants nécessaires pour ChartJS
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
)

function DashboardHoursPage() {
  const navigate = Route.useNavigate()

  const today = new Date()
  const oneMonthBefore = new Date()
  oneMonthBefore.setMonth(today.getMonth() - 1)

  const [currentTimeRange, setCurrentTimeRange] = useState<number>(-1)
  const [currentDateStart, setCurrentDateStart] = useState<Date>(oneMonthBefore)
  const [currentDateEnd, setCurrentDateEnd] = useState<Date>(today)

  const [selectedStores, setSelectedStores] = useState<number[]>([])
  const [selectedDays, setSelectedDays] = useState<DayOfWeek[]>([
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ])
  const [selectedPlage, setSelectedPlage] = useState<'AM' | 'PM' | 'DAY'>('DAY')
  const { data: hoursData } = useHoursData(
    selectedDays,
    selectedStores,
    selectedPlage,
    currentDateStart,
    currentDateEnd,
  )
  const { data: timeSpentData } = useTimeSpentDatas(
    selectedDays,
    selectedStores,
    selectedPlage,
    currentDateStart,
    currentDateEnd,
  )
  const hoursDataPlusOne = useMemo(() => {
    if (!hoursData || hoursData.length === 0) {
      return []
    }
    return [
      ...hoursData,
      {
        amount: hoursData[0].amount,
        count: hoursData[0].count,
        time: selectedPlage === 'AM' ? '12:00' : '24:00',
      },
    ] // Make the chart loop (more intuitive for the user)
  }, [hoursData, selectedPlage])

  const timeSpendDatasPlusOne = useMemo(() => {
    if (!timeSpentData || timeSpentData.length === 0) {
      return []
    }
    return [
      ...timeSpentData,
      {
        averageTimeInDesk: timeSpentData[0].averageTimeInDesk,
        averageTimeInShop: timeSpentData[0].averageTimeInShop,
        time: selectedPlage === 'AM' ? '12:00' : '24:00',
      },
    ] // Make the chart loop (more intuitive for the user)
  }, [timeSpentData, selectedPlage])

  const navigateBack = () => navigate({ to: '/dashboard' })

  // Préparation des données pour le diagramme
  const chartDataCount = useMemo(
    () => ({
      datasets: [
        {
          label: 'Nombre de paniers vendus',
          data: hoursDataPlusOne.map((d) => d.count),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          fill: true,
          tension: 0.5,
        },
      ],
      labels: hoursDataPlusOne.map((d) => `${d.time}`),
    }),
    [hoursDataPlusOne],
  )

  const chartDataAmount = useMemo(
    () => ({
      datasets: [
        {
          label: 'Valeur totale des paniers vendus',
          data: hoursDataPlusOne.map((d) => d.amount),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          fill: true,
          tension: 0.5,
        },
      ],
      labels: hoursDataPlusOne.map((d) => `${d.time}`),
    }),
    [hoursDataPlusOne],
  )

  const chartDataTimeSpent = useMemo(
    () => ({
      datasets: [
        {
          label: 'Temps passé dans le magasin',
          data: timeSpendDatasPlusOne.map((d) => d.averageTimeInShop),
          borderColor: 'rgb(75, 192, 192)',
          backgroundColor: 'rgba(75, 192, 192, 0.2)',
          fill: true,
          tension: 0.5,
        },
        {
          label: 'Temps passé à la caisse',
          data: timeSpendDatasPlusOne.map((d) => d.averageTimeInDesk),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          fill: true,
          tension: 0.5,
        },
        {
          label: 'temps total',
          data: timeSpendDatasPlusOne.map((d) => d.averageTimeInDesk + d.averageTimeInShop),
          borderColor: 'rgb(255, 99, 132)',
          backgroundColor: 'rgba(255, 99, 132, 0.2)',
          fill: true,
          tension: 0.5,
        },
      ],
      labels: timeSpendDatasPlusOne.map((d) => `${d.time}`),
    }),
    [timeSpendDatasPlusOne],
  )

  // Options pour le diagramme
  const optionsCount = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: `Nombre de paniers vendus par tranche de ${
            selectedPlage !== 'DAY' ? '5' : '10'
          } minutes`,
          font: {
            size: 24, // Adjusts the font size
          },
          align: 'start' as const, // Aligns the title to the left
        },
      },
      scales: {
        y: {
          beginAtZero: true,
        },
      },
    }),
    [selectedPlage],
  )

  const optionsAmount = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        responsive: true,
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: `Valeur totale des paniers vendus par tranche de ${
            selectedPlage !== 'DAY' ? '5' : '10'
          } minutes (€)`,
          font: {
            size: 24, // Adjusts the font size
          },
          align: 'start' as const, // Aligns the title to the left
        },
      },
      scales: {
        y: {
          beginAtZero: true,
        },
      },
    }),
    [selectedPlage],
  )

  const timeSpentDatasOptions = useMemo(
    () => ({
      maintainAspectRatio: false,
      plugins: {
        responsive: true,
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: 'Temps moyen passé en magasin et en caisse',
          font: {
            size: 24, // Adjusts the font size
          },
          align: 'start' as const, // Aligns the title to the left
        },
      },
      scales: {
        y: {
          beginAtZero: true,
        },
      },
    }),
    [],
  )

  const dateState: DateStateProps = useMemo(
    () => ({
      timeRanges: {
        all: timeRanges.filter((t) => t.value !== 0),
        current: currentTimeRange,
        set: setCurrentTimeRange,
      },
      currentDate: {
        start: currentDateStart,
        setStart: setCurrentDateStart,
        end: currentDateEnd,
        setEnd: setCurrentDateEnd,
      },
    }),
    [currentTimeRange, currentDateStart, currentDateEnd],
  )

  return (
    <Page sx={fonts.roboto}>
      <div style={{ alignItems: 'center', display: 'flex', paddingBottom: 20 }}>
        <IconButton onClick={navigateBack}>
          <ArrowLeft
            style={{
              color: 'black',
              height: '24px',
              width: '24px',
            }}
          />
        </IconButton>
        <Stack>
          <div
            style={{
              display: 'flex',
              flex: 1,
              flexDirection: 'column',
              height: '100%',
              justifyContent: 'center',
            }}
          >
            <Typography style={{ ...fonts.pageTitle, paddingLeft: 20 }} flexGrow={1}>
              Données temporelles
            </Typography>
          </div>
        </Stack>
      </div>
      <div>
        <Stack gap={1} p={1} flexDirection="row">
          <FourDatePicker dateState={dateState} />
          <StoresPicker
            onlyBoutiques
            allowAllStores
            setCurrentStores={(stores) => {
              setSelectedStores(stores)
            }}
            defaultStore={0}
          />
          <div style={{ display: 'flex', flex: 2, gap: 10, margin: 6 }}>
            <DayPicker setCurrentDays={setSelectedDays} />

            <FormControl fullWidth>
              <Select
                onChange={(e) => {
                  setSelectedPlage(e.target.value as 'AM' | 'PM' | 'DAY')
                }}
                defaultValue="DAY"
              >
                <MenuItem value="AM">Matin</MenuItem>
                <MenuItem value="PM">Après-midi</MenuItem>
                <MenuItem value="DAY">Journée</MenuItem>
              </Select>
            </FormControl>
          </div>
        </Stack>
      </div>
      <div style={{ minHeight: 400 }} className={styles.smallWhiteCards}>
        <Line data={chartDataCount} options={optionsCount} />
      </div>
      <div style={{ minHeight: 400 }} className={styles.smallWhiteCards}>
        <Line data={chartDataAmount} options={optionsAmount} />
      </div>
      <div style={{ minHeight: 400 }} className={styles.smallWhiteCards}>
        <Line data={chartDataTimeSpent} options={timeSpentDatasOptions} />
      </div>
    </Page>
  )
}
